/**
 * moduleRegistry — Vibe Designer Modules tab (Lever 1).
 *
 * The host's declarative map of module_id → how to render it. ModuleHostPane
 * reads this to (a) populate the module-selection dropdown and (b) lazy-mount
 * the selected module's SPA. Adding a module = one entry here + a row in the
 * backend MODULE_METADATA (routes/modules.py) + a per-tenant module-permission
 * row. Modules permitted by the backend but ABSENT here are not shown (the
 * host can't render what it has no loader for) — mirrors the backend's own
 * "drop unknown ids" rule.
 *
 * Loading model: React.lazy + the vite alias direct-source-import (M3a
 * Option α), consistent with how the Briefing Room has always mounted. Each
 * entry may supply a `Provider` wrapper (service-binding context) so the host
 * stays module-agnostic.
 */
import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent, ReactNode } from 'react';
import { BriefingRoomProvider } from '@briefing-room-module';

export interface ModuleRegistryEntry {
  moduleId: string;
  /** Fallback label for the dropdown when the backend doesn't supply a
   * display_name (the permitted-modules response normally does). */
  displayName: string;
  /** Lazy-loaded module SPA (default export = the module's root component). */
  loader: LazyExoticComponent<ComponentType>;
  /** Optional service-binding context wrapper mounted above the SPA. */
  Provider?: ComponentType<{ children: ReactNode }>;
}

export const MODULE_REGISTRY: Record<string, ModuleRegistryEntry> = {
  'gamemagick.briefing-room': {
    moduleId: 'gamemagick.briefing-room',
    displayName: 'The Briefing Room',
    loader: lazy(() =>
      import('@briefing-room-module').then((mod) => ({ default: mod.App })),
    ),
    Provider: BriefingRoomProvider,
  },
  // Lever 2 (2026-06-29) — n8n Workflows module. In-src (NOT a separate
  // apps/ package): not standalone-capable, so it ships no
  // build/alias/Provider. Default export = the App. Appears in the dropdown
  // only when the backend permits 'gamemagick.n8n-workflows' for the tenant.
  // ⛔ NOT read-only (this comment said "read-first" until 2026-11-01): it can
  // CREATE, UPDATE, ACTIVATE, RUN and DELETE workflows on the tenant's n8n.
  'gamemagick.n8n-workflows': {
    moduleId: 'gamemagick.n8n-workflows',
    displayName: 'n8n Workflows',
    loader: lazy(() => import('./n8n-workflows/public')),
  },
  // Lever 2 (2026-06-30) — GoHighLevel module. In-src (not
  // standalone-capable): no build/alias/Provider. Default export = the App.
  // Appears in the dropdown only when the backend permits 'gamemagick.ghl'.
  // ⛔⛔ NOT read-only (this comment said "read-first" until 2026-11-01), and it
  // is the widest-reaching write surface here: it DELETES contacts and
  // opportunities, removes tags, creates/updates both, books and CANCELS
  // appointments, and SENDS MESSAGES to real customers.
  'gamemagick.ghl': {
    moduleId: 'gamemagick.ghl',
    displayName: 'GoHighLevel',
    loader: lazy(() => import('./ghl/public')),
  },
  // 2026-07-25 — Veritas Kanban board module. In-src (not
  // standalone-capable): no build/alias/Provider. Default export = the App.
  // Appears only when the backend permits 'gamemagick.veritas'.
  // ⛔ NOT read-only (said "read-first" until 2026-11-01): creates, patches and
  // DELETES board tasks.
  'gamemagick.veritas': {
    moduleId: 'gamemagick.veritas',
    displayName: 'Veritas Board',
    loader: lazy(() => import('./veritas/public')),
  },
  // P9 M1 (2026-07-02) — Supabase module. In-src (read-only + not
  // standalone-capable): no build/alias/Provider. Default export = the App.
  // Appears only when the backend permits 'gamemagick.supabase'.
  'gamemagick.supabase': {
    moduleId: 'gamemagick.supabase',
    displayName: 'Supabase',
    loader: lazy(() => import('./supabase/public')),
  },
  // P9 M2 (2026-07-02) — Peptide Project backend-management module (CUSTOM).
  // In-src: no build/alias/Provider. Default export = the App. Appears only
  // when the backend permits 'gamemagick.peptides-admin'.
  'gamemagick.peptides-admin': {
    moduleId: 'gamemagick.peptides-admin',
    displayName: 'Peptide Project',
    loader: lazy(() => import('./peptides-admin/public')),
  },
  // HFM B6.3 (2026-08-10) — HealFromMold backend-management module (CUSTOM,
  // peptides-admin twin). In-src: no build/alias/Provider. Default export =
  // the App. Appears only when the backend permits 'gamemagick.healfrommold'
  // (santabarbara only today).
  'gamemagick.healfrommold': {
    moduleId: 'gamemagick.healfrommold',
    displayName: 'HealFromMold',
    loader: lazy(() => import('./healfrommold-admin/public')),
  },
  // 2026-07-03 — ComfyUI image-generation module (shared DGX1 instance via
  // the /api/comfyui backend proxy; curated SFW+NSFW workflows; per-tenant
  // workspace image library). In-src: no build/alias/Provider.
  'gamemagick.comfyui': {
    moduleId: 'gamemagick.comfyui',
    displayName: 'ComfyUI',
    loader: lazy(() => import('./comfyui/public')),
  },
  // P6 A3 (2026-09-05) — Inscriptions: the handwriting arbitration surface for
  // auction photographs (queue viewer + truth writer over the project's
  // sales/<sale>/ files via the /api/inscriptions backend). In-src: no
  // build/alias/Provider. Appears only when the backend permits
  // 'gamemagick.inscriptions' (laketahoe/HDCC only today).
  'gamemagick.inscriptions': {
    moduleId: 'gamemagick.inscriptions',
    displayName: 'Inscriptions',
    loader: lazy(() => import('./inscriptions/public')),
  },
  // 2026-07-03 — Mattermost module (Variant B API module: per-tenant bot via
  // the /api/mattermost backend proxy; team-scoped by Mattermost itself;
  // confirm-gated posting). In-src: no build/alias/Provider.
  'gamemagick.mattermost': {
    moduleId: 'gamemagick.mattermost',
    displayName: 'Mattermost',
    loader: lazy(() => import('./mattermost/public')),
  },
  // 2026-07-04 — Nextcloud module (per-tenant docs-{tenant} instance via the
  // /api/nextcloud backend proxy; WebDAV file browser). In-src.
  // ⛔ NOT read-only (said "read-first" until 2026-11-01): uploads, mkdir,
  // share, archive-extract, and DELETE of the tenant's files.
  'gamemagick.nextcloud': {
    moduleId: 'gamemagick.nextcloud',
    displayName: 'Nextcloud',
    loader: lazy(() => import('./nextcloud/public')),
  },
  // 2026-07-04 — Mailcow module (shared instance via the /api/mailcow backend
  // proxy; {TENANT_ID}- prefix-scoped mailboxes/aliases). In-src.
  // ⛔⛔ NOT read-only (said "read-first" until 2026-11-01): it edits and
  // DELETES mailboxes, and it SENDS MAIL. Outbound email leaves the platform.
  'gamemagick.mailcow': {
    moduleId: 'gamemagick.mailcow',
    displayName: 'Mailcow',
    loader: lazy(() => import('./mailcow/public')),
  },
  // 2026-07-29 (P0) — Google Drive module. PER-USER OAuth: each tenant user
  // connects their OWN Google account via the /api/google-drive backend proxy
  // (per-user encrypted refresh token, server-side). In-src. P0 = connect
  // spine; browse is P1. Appears only when the backend permits
  // 'gamemagick.google-drive'.
  'gamemagick.google-drive': {
    moduleId: 'gamemagick.google-drive',
    displayName: 'Google Drive',
    loader: lazy(() => import('./google-drive/public')),
  },
  // 2026-07-30 (P0) — Doc Studio module. Reproduces Typeset's draft->design->
  // export: a block-model document renders to a branded PDF server-side via
  // WeasyPrint (/api/doc-studio proxy). In-src: no build/alias/Provider. P0 =
  // render spine (sample -> PDF); the 3-pane WYSIWYG editor is P1. Appears only
  // when the backend permits 'gamemagick.doc-studio'.
  'gamemagick.doc-studio': {
    moduleId: 'gamemagick.doc-studio',
    displayName: 'Doc Studio',
    loader: lazy(() => import('./doc-studio/public')),
  },
  // 2026-07-31 — Web Crawler module. Crawl industry/vertical sites via the SHARED
  // crawl4ai service (/api/crawl proxy, Bearer server-side) → save clean markdown
  // into the project (research/*.md) for the existing MIND ingest. In-src: no
  // build/alias/Provider. Appears only when the backend permits 'gamemagick.crawl4ai'.
  'gamemagick.crawl4ai': {
    moduleId: 'gamemagick.crawl4ai',
    displayName: 'Web Crawler',
    loader: lazy(() => import('./crawl4ai/public')),
  },
  // 2026-07-04 — Projects module (manage projects + project templates). In-src:
  // uses the host's /api/project-templates (v3_project_templates catalog) +
  // /api/projects + /api/agent (auto-descriptions) + the shared projectStore.
  // No build/alias/Provider. Appears only when the backend permits
  // 'gamemagick.projects'.
  'gamemagick.projects': {
    moduleId: 'gamemagick.projects',
    displayName: 'Projects',
    loader: lazy(() => import('./projects/public')),
  },
  // P1.4 (2026-07-09) — Payload CMS. In-src, no Provider. Also opens the shared
  // payload.gamemagick.com in a new tab (X-Frame-Options blocks iframe embed);
  // SSO scopes the operator to their tenant. Appears only when the backend
  // permits 'gamemagick.payload'.
  // ⛔ This said "link-out … no backend proxy" until 2026-11-01. It HAS one:
  // routes/payload.py (~587 lines), which also serves the Pi/voice agents, and
  // payloadApi.ts makes 11 write calls (create, patch, publish, DELETE) through
  // it. ⚠ payload.module.json still omits `api_prefix` — the only manifest that
  // does; fix it there too.
  'gamemagick.payload': {
    moduleId: 'gamemagick.payload',
    displayName: 'Payload CMS',
    loader: lazy(() => import('./payload/public')),
  },
  // 2026-07-19 (T-B) — Telephony AI-receptionist module. In-src (needs the
  // host's /api/telephony proxy to the shared DGX1 bridge; not standalone):
  // no build/alias/Provider. Status panel + LLM/voice picker + simulated test
  // call. Appears only when the backend permits 'gamemagick.telephony'.
  'gamemagick.telephony': {
    moduleId: 'gamemagick.telephony',
    displayName: 'Telephony',
    loader: lazy(() => import('./telephony/public')),
  },
};

/** Ordered list form for the dropdown's "degrade open" fallback (when the
 * backend permitted-modules list hasn't loaded yet or errored). */
export const REGISTRY_ENTRIES: ModuleRegistryEntry[] = Object.values(MODULE_REGISTRY);
